import { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelectActions } from "./MultiSelectActions";

export interface EntityGridProps<T> {
  data: T[];
  isLoading: boolean;
  renderItem: (item: T) => ReactNode;
  emptyMessage?: string;
  isMultiSelectMode?: boolean;
  onToggleMultiSelect?: () => void;
  onToggleItemSelection?: (id: string) => void;
  isItemSelected?: (id: string) => boolean;
  onDeleteSelected?: () => void;
  onMergeSelected?: () => void;
  onMassUpdate?: () => void;
  selectedCount?: number;
  selectAll?: () => void;
  onCardClick?: (id: string) => void;
  disableLinks?: boolean;
  cardMinWidth?: number;
}

export const EntityGrid = <T extends { id: string }>({
  data,
  isLoading,
  renderItem,
  emptyMessage = "No items found matching your search criteria.",
  isMultiSelectMode = false,
  onToggleMultiSelect,
  onToggleItemSelection,
  isItemSelected,
  onDeleteSelected,
  onMergeSelected,
  onMassUpdate,
  selectedCount = 0,
  selectAll,
  onCardClick,
  disableLinks = false,
  cardMinWidth = 280,
}: EntityGridProps<T>) => {
  const gridTemplateColumns = `repeat(auto-fill, minmax(min(${cardMinWidth}px, 100%), 1fr))`;

  if (isLoading) {
    return (
      <div className="grid gap-5 grid-cols-1"
           style={{ gridTemplateColumns }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-sm shadow-sm p-5 animate-pulse">
            <div className="flex justify-between items-center mb-3">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm"></div>
            </div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
            <div className="space-y-3">
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Only show empty message if not loading and data is truly empty (after initial fetch)
  if (!data || data.length === 0) {
    return (
      <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-sm shadow-sm border border-gray-100 dark:border-gray-700">
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div>
      {onToggleMultiSelect && (
        <div className="mb-4">
          <MultiSelectActions
            isMultiSelectMode={isMultiSelectMode}
            onToggleMultiSelect={onToggleMultiSelect}
            onDeleteSelected={onDeleteSelected || (() => {})}
            onMergeSelected={onMergeSelected}
            onMassUpdate={onMassUpdate}
            selectedCount={selectedCount}
            selectAll={selectAll}
          />
        </div>
      )}
      <div className="grid gap-5 overflow-visible pb-8 pr-8 grid-cols-1"
           style={{ gridTemplateColumns }}>
        {data.map(item => (
          <div key={item.id} className="relative w-full h-full"
            {...(isMultiSelectMode && onCardClick ? {
              onClick: (e) => {
                if ((e.target as HTMLElement).closest('input[type="checkbox"]')) return;
                onCardClick(item.id);
              },
              style: { cursor: 'pointer' },
            } : {})}
          >
            {isMultiSelectMode && onToggleItemSelection && (
              <div className="absolute top-2 right-2 z-20 pointer-events-auto" onClick={e => e.stopPropagation()}>
                <Checkbox
                  checked={isItemSelected?.(item.id) || false}
                  onCheckedChange={() => onToggleItemSelection(item.id)}
                  aria-label={`Select ${item.id}`}
                />
              </div>
            )}
            {renderItem({ ...item, disableLinks: disableLinks || isMultiSelectMode })}
          </div>
        ))}
      </div>
    </div>
  );
};
